import { HocuspocusProvider, WebSocketStatus } from "@hocuspocus/provider";
/**
 * A legacy (Hocuspocus 2.15.3) provider that heals itself after a refused
 * sign-in (NET-BUG-28) and whose Retry rejoins at once (NET-BUG-31). Secure
 * mode never uses it.
 *
 * It relies on this pinned Hocuspocus behaviour, which
 * `test/unit/legacy-provider-internals.spec.ts` fails on if it changes:
 * - a refusal (`authenticationFailed`) takes the provider out of its socket's
 *   `providerMap` and leaves the socket running;
 * - the socket hands incoming frames only to providers in that map, so a
 *   detached provider that signs in again still sends but never hears back;
 * - the provider's `open` and `status` listeners on the socket survive that,
 *   so a reopened socket does sign the detached provider in again;
 * - `provider.connect()` puts the provider back in the map and opens a closed
 *   socket;
 * - each socket open asks for the ticket once and sends nothing for that open
 *   until the ask settles;
 * - between failed connects the socket stays "connecting" with no WebSocket
 *   while it waits out its own back-off (1 s, doubling to 30 s), and
 *   `provider.connect()` opens nothing then: it opens a socket only from
 *   "disconnected";
 * - `socket.connect()` drops that wait, resets the back-off and makes its
 *   first try at once, inside the call.
 * So a refusal closes the refused socket at once and, after a short wait,
 * rejoins with `provider.connect()`. It never reconnects the socket alone.
 *
 * Retry (`retry()`) drops our own wait and calls `provider.connect()` only
 * for a detached provider. When no socket is open because Hocuspocus is
 * waiting between failed connects, it also calls `socket.connect()`, so one
 * socket opens at once. A socket that is already opening or open is left
 * alone, and an attached provider is never signed in a second time.
 */
/** Refusals in a row before the page stops and shows Retry, as before. */
export const LEGACY_RECOVERY_MAX_REFUSALS = 3;
/** The wait before the n-th rejoin is n times this: 1 s, then 2 s. */
export const LEGACY_RECOVERY_BACKOFF_STEP_MS = 1_000;
export class LegacyProviderRecovery {
    provider;
    getTicket;
    awaitingAuthorization;
    refusals = 0;
    timer = undefined;
    stopped = false;
    noAccess = false;
    /** Ticket asks so far; only the newest one's answer is used. */
    asks = 0;
    constructor(input) {
        this.getTicket = input.getTicket;
        this.awaitingAuthorization = input.awaitingAuthorization;
        this.provider = new HocuspocusProvider({
            url: input.url,
            name: input.name,
            document: input.document,
            token: this.ticket,
        });
        this.provider.on("authenticationFailed", this.onRefused);
        this.provider.on("synced", this.onSynced);
    }
    /**
     * The app stops the connection. No recovery runs until the app rejoins by
     * itself and that join succeeds.
     */
    stop = () => {
        this.stopped = true;
        this.cancel();
        this.provider.disconnect();
    };
    /**
     * The person pressed Retry: rejoin now, without waiting out our rejoin
     * wait or Hocuspocus's back-off (NET-BUG-31).
     */
    retry = () => {
        this.cancel();
        // Only a detached provider (a refused sign-in took it out of the map)
        // needs `provider.connect()`. An attached one signs in on its socket's
        // next open, and on an open socket it is already signed in: connecting it
        // again would replay that sign-in — a new ticket ask and sync — and a
        // failed ask would then drop a working connection (PR 450 review).
        if (!isAttached(this.provider))
            void this.provider.connect();
        // A socket is now opening or open, unless Hocuspocus is waiting between
        // failed connects: `provider.connect()` does not end that wait. Only then
        // start the socket again, which ends the wait and resets the back-off.
        // An opening or open socket is never touched, so one socket opens.
        const socket = this.provider.configuration.websocketProvider;
        if (socket.webSocket === null &&
            socket.status !== WebSocketStatus.Connected) {
            void socket.connect();
        }
    };
    /** The document is torn down or replaced: nothing may rejoin it. */
    dispose() {
        this.cancel();
        this.provider.off("authenticationFailed", this.onRefused);
        this.provider.off("synced", this.onSynced);
    }
    /**
     * Never an empty sign-in: without a ticket the attempt fails before any
     * sign-in is sent, and Hocuspocus reports it as a refusal.
     *
     * Hocuspocus asks for a ticket once per socket open and waits for it before
     * it sends anything for that open. When a newer open has asked meanwhile,
     * this answer is stale: a throw would refuse, and so detach, the CURRENT
     * provider; a ticket would send a second sign-in with an old single-use
     * ticket and a second sync step 1. The pinned provider cannot tell the two
     * opens apart, and we do not patch it for this, so the only safe stale
     * answer is one that never settles: the old open stops here, and only the
     * newest ask may set `noAccess` or throw.
     */
    ticket = async () => {
        const ask = ++this.asks;
        this.noAccess = false;
        let ticket;
        try {
            ticket = await this.getTicket();
        }
        catch (error) {
            if (ask !== this.asks)
                return never();
            throw error;
        }
        if (ask !== this.asks)
            return never();
        if (ticket)
            return ticket;
        this.noAccess = true;
        throw new Error("No collaboration ticket: no access");
    };
    onRefused = () => {
        this.cancel();
        // Close the refused socket so Hocuspocus does not reopen it for a provider
        // that is no longer attached. Hocuspocus detaches the provider right after
        // this event and sends its "left" presence on the socket; closing after
        // that keeps the frame from being queued for the next connection.
        const socket = this.provider.configuration.websocketProvider;
        queueMicrotask(() => socket.disconnect());
        // The next "synced" then marks a real join, even when the socket died
        // without reporting its close.
        this.provider.synced = false;
        if (this.stopped || this.noAccess || this.awaitingAuthorization())
            return;
        this.refusals += 1;
        if (this.refusals >= LEGACY_RECOVERY_MAX_REFUSALS)
            return;
        this.timer = setTimeout(this.rejoin, LEGACY_RECOVERY_BACKOFF_STEP_MS * this.refusals);
    };
    rejoin = () => {
        this.timer = undefined;
        // A Retry has already rejoined, or an access check started meanwhile.
        if (this.awaitingAuthorization() || isAttached(this.provider))
            return;
        void this.provider.connect();
    };
    /** A good join, whoever started it: signed in and caught up. */
    onSynced = ({ state }) => {
        if (!state || !this.provider.isAuthenticated)
            return;
        this.refusals = 0;
        this.stopped = false;
        this.cancel();
    };
    cancel() {
        clearTimeout(this.timer);
        this.timer = undefined;
    }
}
/** A promise that never settles: what a stale ticket ask answers. */
function never() {
    return new Promise(() => undefined);
}
function isAttached(provider) {
    const { providerMap } = provider.configuration.websocketProvider.configuration;
    return providerMap.get(provider.configuration.name) === provider;
}
//# sourceMappingURL=legacy-provider-recovery.js.map